/**
 * Cliente HTTP para documentos-fonte de um caso (E8.F2 backend → E8.F3 UI).
 *
 * Espelha apps/api/types/case_source_documents.py e
 * apps/api/runtime/case_source_documents.py. Cobre:
 * - uploadDocument: POST multipart /casos/:case_id/documents?kind=...
 * - listDocuments: GET /casos/:case_id/documents (filtro opcional por kind)
 * - getDocument: GET /documents/:doc_id
 * - reextractDocument: POST /documents/:doc_id/extract
 * - deleteDocument: DELETE /documents/:doc_id
 * - getDocumentPageUrl: helper de path para o canvas Fabric.js (E8.F4)
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { nomeDoAnexo, type ArquivoBaixado } from './http_download';

export type SourceDocumentKind = 'standard' | 'contested';

export type ExtractionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

export type ExtractionMethod = 'llm_vision' | 'manual' | 'mixed';

/**
 * Metadados forenses propostos a partir de uma CCB (E23.F2/F3). Espelha
 * apps/api/types/ccb_metadata.py::CcbMetadata. Proposta para pré-preenchimento
 * — confirmação humana obrigatória antes de virar dado do caso (LGPD).
 */
export interface CcbMetadataData {
  sbr_identification: string | null;
  cpf: string | null;
  author: string | null;
  process_number: string | null;
  defendant: string | null;
  contract: string | null;
  extraction_source: 'regex' | 'vision';
}

export interface SourceDocumentRecordData {
  id: string;
  case_id: string;
  usuario_id: string;
  kind: SourceDocumentKind;
  // E50.F3/F4: número da CCB declarada no slot do portal (null no fluxo do perito).
  ccb_number?: string | null;
  original_filename: string;
  blob_path: string;
  mime_type: string;
  size_bytes: number;
  page_count: number | null;
  has_embedded_images: boolean;
  extraction_status: ExtractionStatus;
  extraction_method: ExtractionMethod | null;
  extraction_error: string | null;
  extraction_started_at: string | null;
  extraction_completed_at: string | null;
  created_at: string;
  deleted_at?: string | null;
  extracted_metadata?: CcbMetadataData | null;
}

export interface DocumentUploadPayload {
  file: File;
  kind: SourceDocumentKind;
  /** E50.F4: nº da CCB do slot do portal (só com kind='contested'). */
  ccbNumber?: string;
  onProgress?: (percent: number) => void;
}

// Erros tipados -----------------------------------------------------------

export type DocumentErrorReason =
  | 'case_not_found'
  | 'document_not_found'
  | 'unsupported_mime'
  | 'file_too_large'
  | 'file_empty'
  | 'page_number_invalid'
  | 'page_not_found'
  | 'render_failed'
  | 'unreadable'
  // E50.F4: nº de CCB repetido entre documentos vivos do caso (409 do backend).
  | 'ccb_duplicate'
  | 'validation'
  | 'network'
  | 'rate_limited'
  | 'unknown';

export class DocumentError extends Error {
  public readonly reason: DocumentErrorReason;

  constructor(reason: DocumentErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'DocumentError';
  }
}

const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_UNPROCESSABLE = 422;

function isNetworkError(err: AxiosError): boolean {
  return err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
}

function detailString(err: AxiosError): string {
  const data = err.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : '';
}

const NOT_FOUND_BY_DETAIL: Record<string, DocumentErrorReason> = {
  CASE_NOT_FOUND: 'case_not_found',
  PAGE_NOT_FOUND: 'page_not_found',
};

const BAD_REQUEST_BY_DETAIL: Record<string, DocumentErrorReason> = {
  UNSUPPORTED_MIME: 'unsupported_mime',
  FILE_TOO_LARGE: 'file_too_large',
  FILE_EMPTY: 'file_empty',
  PAGE_NUMBER_INVALID: 'page_number_invalid',
  // E23.F3: CCB .docx ilegível no preview (F-E23.F3-03).
  DOCUMENT_UNREADABLE: 'unreadable',
};

const HTTP_CONFLICT = 409;

function reasonFromHttpStatus(
  httpStatus: number | undefined,
  detail: string,
): DocumentErrorReason | null {
  if (httpStatus === HTTP_NOT_FOUND) {
    return NOT_FOUND_BY_DETAIL[detail] ?? 'document_not_found';
  }
  if (httpStatus === HTTP_BAD_REQUEST) {
    return BAD_REQUEST_BY_DETAIL[detail] ?? 'validation';
  }
  if (httpStatus === HTTP_TOO_MANY_REQUESTS) return 'rate_limited';
  if (httpStatus === HTTP_CONFLICT && detail === 'CCB_NUMBER_ALREADY_IN_CASE') {
    return 'ccb_duplicate';
  }
  if (httpStatus === HTTP_UNPROCESSABLE) {
    return detail.startsWith('RENDER_FAILED') ? 'render_failed' : 'validation';
  }
  return null;
}

export function classifyDocumentError(err: unknown): DocumentError {
  if (!axios.isAxiosError(err)) return new DocumentError('unknown');
  const axiosErr = err as AxiosError;
  const detail = detailString(axiosErr);
  const reason = reasonFromHttpStatus(axiosErr.response?.status, detail);
  if (reason !== null) {
    return new DocumentError(reason, reason === 'render_failed' ? detail : undefined);
  }
  if (isNetworkError(axiosErr)) return new DocumentError('network');
  return new DocumentError('unknown');
}

// HTTP --------------------------------------------------------------------

/**
 * Extrai metadados de uma CCB `.docx` para pré-preenchimento, sem persistir
 * (E23.F3). Stateless: não há caso ainda. POST multipart /ccb/metadata-preview.
 */
export async function previewCcbMetadata(
  client: AxiosInstance,
  file: File,
): Promise<CcbMetadataData> {
  const form = new FormData();
  form.append('file', file);
  try {
    const { data } = await client.post<CcbMetadataData>('/ccb/metadata-preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function uploadDocument(
  client: AxiosInstance,
  caseId: string,
  payload: DocumentUploadPayload,
): Promise<SourceDocumentRecordData> {
  const form = new FormData();
  form.append('file', payload.file);
  try {
    const { data } = await client.post<SourceDocumentRecordData>(
      `/casos/${caseId}/documents`,
      form,
      {
        params:
          payload.ccbNumber !== undefined && payload.ccbNumber !== ''
            ? { kind: payload.kind, ccb_number: payload.ccbNumber }
            : { kind: payload.kind },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (payload.onProgress && evt.total != null && evt.total > 0) {
            payload.onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      },
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function listDocuments(
  client: AxiosInstance,
  caseId: string,
  kind?: SourceDocumentKind,
): Promise<SourceDocumentRecordData[]> {
  try {
    const { data } = await client.get<SourceDocumentRecordData[]>(
      `/casos/${caseId}/documents`,
      kind ? { params: { kind } } : undefined,
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

/**
 * Baixa o arquivo ORIGINAL do documento — o que foi enviado ao caso (E65.F6).
 *
 * Não é a imagem da página nem o recorte da assinatura, que são derivados. O
 * `case_id` vai na rota porque o servidor confere que o documento é daquele
 * caso: a RLS isola tenants, não dois casos do mesmo escritório.
 *
 * Devolve o nome do `Content-Disposition` junto com os bytes (C04): é o
 * servidor quem sanea o nome e corrige a extensão pelo mime da ingestão, e
 * usar o `original_filename` da listagem desfaria isso na hora de salvar.
 */
export async function baixarArquivoOriginal(
  client: AxiosInstance,
  caseId: string,
  docId: string,
  nomeSugerido: string,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/documents/${docId}/arquivo`, {
      responseType: 'blob',
    });
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(resposta.headers['content-disposition'], nomeSugerido),
    };
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function getDocument(
  client: AxiosInstance,
  docId: string,
): Promise<SourceDocumentRecordData> {
  try {
    const { data } = await client.get<SourceDocumentRecordData>(`/documents/${docId}`);
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function reextractDocument(
  client: AxiosInstance,
  docId: string,
): Promise<SourceDocumentRecordData> {
  try {
    const { data } = await client.post<SourceDocumentRecordData>(`/documents/${docId}/extract`);
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function deleteDocument(client: AxiosInstance, docId: string): Promise<void> {
  try {
    await client.delete(`/documents/${docId}`);
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export function getDocumentPageUrl(docId: string, pageNumber: number): string {
  return `/documents/${docId}/pages/${pageNumber}`;
}

// Helpers de UI -----------------------------------------------------------

/**
 * Estados terminais: o polling pode parar quando o documento atinge um deles.
 */
const TERMINAL_STATUSES: ReadonlySet<ExtractionStatus> = new Set([
  'completed',
  'failed',
  'skipped',
]);

export function isExtractionTerminal(status: ExtractionStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Classifica `extraction_error` para mapear a string de UI correta. // ux-neutrality:allow doc-comment
 * Reconhece o sinal de rate limit propagado pelo backend (E8.F2.T05). // ux-neutrality:allow doc-comment
 */
export function isRateLimitError(extractionError: string | null): boolean {
  if (typeof extractionError !== 'string') return false;
  const normalized = extractionError.toLowerCase();
  return normalized.includes('rate') || normalized.includes('429');
}

/** E70.F2: recusa da CONTA do provedor — a causa está fora do produto. */
export type ProviderFailure = 'provider_credit' | 'provider_auth';

/**
 * Reconhece as chaves estáveis que o backend grava tanto em
 * `extraction_error` (`provider_credit`) quanto em `error_reason` da análise
 * (`assistant: provider_credit`). Qualquer outro valor → `null`.
 */
export function classifyProviderFailure(reason: string | null | undefined): ProviderFailure | null {
  if (typeof reason !== 'string') return null;
  if (reason.includes('provider_credit')) return 'provider_credit';
  if (reason.includes('provider_auth')) return 'provider_auth';
  return null;
}
